import type { ServiceManager } from '@/framework/services/service-manager';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { getFontFamilyWithFallback, getPlainTextFontByPretext } from '@/utils/text-kit';
import type { IChartLayer } from '@/version-types';
import { collectChartLayerFonts } from './behaviors/font';

type ChartFontServices = Pick<ServiceManager, 'font'>;

/**
 * 尽力准备图表 Canvas 测量所需的 document 字体。
 *
 * @param services - 提供字体查询与子集加载能力的服务集合
 * @param layer - 需要准备字体的图表图层
 * @returns 所有字体加载尝试完成后 resolve
 */
export const prepareChartLayerDocumentFonts = async (
    services: ChartFontServices,
    layer: IChartLayer,
): Promise<void> => {
    const fontItems = collectChartLayerFonts(layer.data);

    await Promise.allSettled(
        fontItems.map(async ({ fontFamily, text }) => {
            if (!services.font.getByFamily(fontFamily)) return;

            const subsetFamily = await services.font.loadSubset(fontFamily, text, { registerTo: 'document' });
            if (!subsetFamily) return;

            // Canvas 测量仍通过图层映射读取实际注册到 document 的字体族。
            setLayerRuntimeValue(layer, {
                fontFamilyMap: {
                    ...(layer.__runtime?.fontFamilyMap || {}),
                    [fontFamily]: subsetFamily,
                },
            });
        }),
    );
    if (typeof document === 'undefined' || !document.fonts) return;

    await Promise.allSettled(
        fontItems.map(async ({ fontFamily, text }) => {
            const subsetFamily = layer.__runtime?.fontFamilyMap?.[fontFamily];
            const font = getPlainTextFontByPretext({
                fontFamily: getFontFamilyWithFallback(fontFamily, subsetFamily),
                fontSize: 12,
            });
            await document.fonts.load(font, text);
        }),
    );
};
